/**
 * WL-K9 — Exception-First-StB-Cockpit (the differentiator „Wow-Moment"-Screen).
 *
 * A read-only triage board for the Steuerberater: it verdichtet je Mandant alle
 * Risiko-Signale (WL-K10 `document_checks` + EN-16931 `validation.findings`) zu
 * EINER gerankten Arbeitsliste — „N von M ansehen, der Rest ist grün". Grüne Belege
 * werden nicht gelistet; „0 Findings" ist der Erfolgszustand „alles grün", kein
 * Fehler.
 *
 * SICHERHEIT: alle harten Prüfungen (Advisor-Scope + Loop-B-Beta, Cross-Tenant-
 * Isolation, Mutationsfreiheit) liegen server-seitig in den Callables
 * (`getAdvisorCockpitMandanten` / `getAdvisorCockpitWorklist`). Diese Seite ist
 * rein kosmetisch beta-gated (`isLoopBBeta`) — ein Nicht-Beta-Nutzer sieht nichts,
 * und selbst bei Direktaufruf liefert der Server für fremde Mandanten
 * `permission-denied`.
 *
 * Outer/Inner-Split: die äußere Komponente ist das Sichtbarkeits-Gate; die innere
 * (datenladende) Komponente wird nur montiert, wenn der Nutzer Beta ist — so wird
 * keine Cockpit-Query für Nicht-Beta-Nutzer ausgelöst.
 *
 * @module AdvisorCockpitPage
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconAdvisorCockpit,
  IconSuccess,
  IconWarning,
  IconGobd,
  IconMail,
  IconViewOff,
  IconChevronRight,
} from '../icons';
import { useAuth } from '../context/AuthContext';
import { canUseKanzleiCockpit, canUseAdvisorDatev } from '../config/kanzleiAccess';
import { canUseMessaging } from '../config/messagingAccess';
import {
  getAdvisorCockpitMandanten,
  getAdvisorCockpitWorklist,
  type CockpitMandantSummary,
  type CockpitWorklistResult,
  type CockpitItem,
  type CockpitFinding,
} from '../api/advisorCockpitApi';
import { resolveCheckFinding } from '../api/cockpitResolutionApi';
import { resolveReasonLabel } from '../utils/cockpitReason';
import Card from '../components/common/Card';
import Collapsible from '../components/common/Collapsible';
import ClientDatevConfigPanel from '../components/tenant/ClientDatevConfigPanel';
import KontierungVorschlagPanel from '../components/moat/KontierungVorschlagPanel';
import FindingDrilldown from '../components/moat/FindingDrilldown';
import FindingResolveControls from '../components/moat/FindingResolveControls';
import CockpitMessageComposer from '../components/moat/CockpitMessageComposer';

/** Formats a decimal-euro amount for display; null/invalid ⇒ null (caller shows fallback). */
function formatAmount(value: number | null, currency: string): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency && currency.length === 3 ? currency : 'EUR',
    }).format(value);
  } catch {
    return `${value.toFixed(2)} €`;
  }
}

/** Composite selection key for one finding (M-UX-7.4 E1). */
function findingKey(docId: string, checkId: string): string {
  return `${docId}::${checkId}`;
}

/**
 * Renders a single flagged worklist item as a triage row.
 *
 * M-UX-7.4: the row is a roving-tabindex keyboard target and carries the
 * PARENT-owned batch selection surfaces — a tri-state row checkbox (toggles all
 * of the doc's active findings) plus per-finding checkboxes in the drilldown.
 * Selection state lives entirely in {@link MandantWorklist}; this component only
 * reflects it and reports toggles upward. `FindingResolveControls` is embedded
 * unchanged (E3 — byte-identical, it has a second consumer).
 */
const WorklistRow: React.FC<{
  item: CockpitItem;
  mandantUid: string;
  /** Position in the worklist (roving-tabindex index). */
  index: number;
  /** Whether this row currently holds `tabIndex=0` (the single tab stop). */
  isActive: boolean;
  /** Parent-owned set of selected finding keys (`docId::checkId`). */
  selectedKeys: Set<string>;
  /** WL-B: refetches the worklist after a resolve/ignore/reopen write. */
  onWorklistChanged: () => void;
  /** Toggle a single finding's selection. */
  onToggleFinding: (docId: string, checkId: string) => void;
  /** Toggle all active findings of the doc (tri-state row checkbox / Space). */
  onToggleRow: (docId: string) => void;
  /** Sync the roving-tabindex pointer when this row receives focus. */
  onFocusRow: (index: number) => void;
  /** Move keyboard focus by `delta` rows (clamped) — ArrowUp/ArrowDown. */
  onMoveFocus: (delta: number) => void;
  /** Clear the whole selection — Escape. */
  onClearSelection: () => void;
  /** Register the row DOM node so the parent can `focus()` it. */
  registerRow: (index: number, el: HTMLLIElement | null) => void;
}> = ({
  item,
  mandantUid,
  index,
  isActive,
  selectedKeys,
  onWorklistChanged,
  onToggleFinding,
  onToggleRow,
  onFocusRow,
  onMoveFocus,
  onClearSelection,
  registerRow,
}) => {
  const { t } = useTranslation('common');
  const [showKontierung, setShowKontierung] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  // Which finding's "Nachricht an Mandant" composer is currently open (by code); null = closed.
  const [composerFinding, setComposerFinding] = useState<CockpitFinding | null>(null);
  const rowCheckboxRef = useRef<HTMLInputElement>(null);
  const isRed = item.ampel === 'red';
  const amount = formatAmount(item.grossTotal, item.currency);
  // Aktion 1c Cross-Gate-Abhängigkeit (architecture §Beta-Gating): the message
  // is posted INTO the mandant's tenant scope — gate on that scope, not the
  // advisor's own uid.
  const messagingAvailable = canUseMessaging(mandantUid);
  const supplierLabel = item.supplierName || item.invoiceNumber || item.docId;

  // Tri-state row selection over the doc's ACTIVE findings (§0: item.findings
  // is always active-only ⇒ every listed finding is selectable).
  const rowFindingKeys = item.findings.map((f) => findingKey(item.docId, f.checkId));
  const selectedInRow = rowFindingKeys.reduce((n, k) => (selectedKeys.has(k) ? n + 1 : n), 0);
  const allSelected = rowFindingKeys.length > 0 && selectedInRow === rowFindingKeys.length;
  const someSelected = selectedInRow > 0 && !allSelected;
  // `indeterminate` is a DOM property, not an attribute — set it imperatively.
  useEffect(() => {
    if (rowCheckboxRef.current) rowCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  // Keyboard triage (roving-tabindex). Target-Guard: only act when the row
  // itself is the event target — typing in the drilldown note field
  // (event.target = <input>) must NOT trigger row shortcuts (§2.4).
  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
    if (event.target !== event.currentTarget) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        onMoveFocus(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        onMoveFocus(-1);
        break;
      case 'Enter':
        event.preventDefault();
        setShowDetails((prev) => !prev);
        break;
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        onToggleRow(item.docId);
        break;
      case 'Escape':
        event.preventDefault();
        onClearSelection();
        break;
      default:
        break;
    }
  };

  // Three-channel row state so selected / focused / hover never turn to mush
  // (goal #2 / D2). Severity stays on the LEFT BORDER; the row FILL is the
  // SELECTION channel (emerald — the finding-resolution workflow colour), so a
  // batch of selected rows is scannable at a glance without reading each box.
  // Keyboard focus is a separate channel (bright inset RING, below); hover is a
  // third (faint lift). FaeBill's mint ≈ emerald, so states are distinguished by
  // channel + intensity, not hue.
  const anySelected = selectedInRow > 0;
  const severityBorder = isRed ? 'border-l-4 border-red-500' : 'border-l-4 border-amber-400';
  const stateFill = anySelected
    ? 'bg-emerald-500/[0.14]'
    : `${isRed ? 'bg-red-500/5' : 'bg-amber-400/5'} hover:bg-white/[0.03]`;
  const badge = isRed
    ? 'bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)]'
    : 'bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)]';

  return (
    <li
      ref={(el) => registerRow(index, el)}
      tabIndex={isActive ? 0 : -1}
      onFocus={() => onFocusRow(index)}
      onKeyDown={handleRowKeyDown}
      aria-expanded={showDetails}
      className={`rounded-md ${severityBorder} ${stateFill} p-4 transition-colors duration-100 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400`}
    >
      <div className="flex items-start gap-3">
        <label
          className="-m-1 flex h-6 shrink-0 cursor-pointer items-center p-1"
          title={t('cockpit.batch.selectRow', { supplier: supplierLabel })}
        >
          <input
            ref={rowCheckboxRef}
            type="checkbox"
            checked={allSelected}
            onChange={() => onToggleRow(item.docId)}
            aria-label={t('cockpit.batch.selectRow', { supplier: supplierLabel })}
            className="h-4 w-4 shrink-0 rounded border-gray-500 bg-surface-inset accent-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
          />
        </label>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${badge}`}>
              {isRed ? t('cockpit.red') : t('cockpit.yellow')}
            </span>
            <span className="truncate text-sm font-semibold text-primary">
              {supplierLabel}
            </span>
          </div>
          <div className="mt-1 text-xs text-muted">
            {[item.invoiceNumber, item.documentDate].filter(Boolean).join(' · ')}
          </div>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {item.findings.map((f, idx) => (
              <li
                key={`${item.docId}-${f.checkId}-${idx}`}
                className="inline-flex items-center rounded bg-[rgb(var(--surface-inset-rgb)/0.7)] px-2 py-0.5 text-xs text-secondary"
                title={t('cockpit.reasonsLabel')}
              >
                {/* fix cockpit-worklist-amount: worklist-level CockpitFinding
                    never carries `evidence` (cockpitRanking.ts — intentionally
                    omitted server-side), so no `amount` can be interpolated
                    here; the shared helper falls back to the clean, amount-less
                    sentence instead of leaking a raw `{{amount}}` placeholder. */}
                {resolveReasonLabel(t, f.code)}
              </li>
            ))}
          </ul>
          {item.verifiedAgainstOriginal && (
            <div className="mt-2 inline-flex items-center gap-1 text-xs text-[var(--tone-success-fg)]">
              <IconGobd size={12} />
              {t('cockpit.verifiedSeal')}
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className={`text-lg font-bold tabular-nums ${isRed ? 'text-[var(--tone-danger-fg)]' : 'text-[var(--tone-warning-fg)]'}`}>
            {amount ?? <span className="text-sm font-normal text-faint">{t('cockpit.noAmount')}</span>}
          </div>
        </div>
      </div>

      {/* Welle 1 (Cockpit-Intelligenz) Aktion 1a/1b — drill-down evidence +
          per-finding resolve/ignore + "Nachricht an Mandant". Reviewer
          Re-Loop M1: the "wieder öffnen" action for ALREADY resolved/ignored
          findings lives inside FindingDrilldown itself (only place that data
          is available — `item.findings` below is always active-only). */}
      <div className="mt-3 border-t border-hairline pt-3">
        <button
          type="button"
          onClick={() => setShowDetails((prev) => !prev)}
          aria-expanded={showDetails}
          className="text-xs font-medium text-accent-ink hover:text-indigo-200"
        >
          {showDetails
            ? t('cockpit.drilldown.hide', 'Details ausblenden')
            : t('cockpit.drilldown.show', 'Details anzeigen')}
        </button>
        {showDetails && (
          <div className="mt-2 space-y-3">
            <FindingDrilldown
              mandantUid={mandantUid}
              docId={item.docId}
              onResolved={onWorklistChanged}
            />
            <ul className="space-y-2">
              {item.findings.map((f, idx) => {
                const key = findingKey(item.docId, f.checkId);
                // fix cockpit-worklist-amount: same fallback-only resolution
                // as the chip above — no `evidence` at the worklist level.
                const reasonLabel = resolveReasonLabel(t, f.code);
                return (
                  <li
                    key={`${item.docId}-${f.checkId}-${idx}-resolve`}
                    className="rounded bg-[rgb(var(--surface-raised-rgb)/0.4)] p-2"
                  >
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      {/* Fine-grained selection checkbox — parent-owned, sits
                          NEXT TO (never inside) FindingResolveControls (E3). */}
                      <label className="flex min-w-0 cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(key)}
                          onChange={() => onToggleFinding(item.docId, f.checkId)}
                          aria-label={t('cockpit.batch.selectFinding', { reason: reasonLabel })}
                          className="h-4 w-4 shrink-0 rounded border-gray-500 bg-surface-inset accent-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                        />
                        <span className="truncate text-xs font-medium text-secondary">
                          {reasonLabel}
                        </span>
                      </label>
                      {messagingAvailable && (
                        <button
                          type="button"
                          onClick={() => setComposerFinding(f)}
                          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-accent-ink hover:text-indigo-200"
                        >
                          <IconMail size={12} />
                          {t('cockpit.message.title', 'Nachricht an Mandant')}
                        </button>
                      )}
                    </div>
                    <FindingResolveControls
                      mandantUid={mandantUid}
                      docId={item.docId}
                      checkId={f.checkId}
                      resolution={f.resolution}
                      onResolved={onWorklistChanged}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {composerFinding && (
        <CockpitMessageComposer
          mandantUid={mandantUid}
          docId={item.docId}
          code={composerFinding.code}
          invoiceNumber={item.invoiceNumber}
          supplierName={item.supplierName}
          onClose={() => setComposerFinding(null)}
        />
      )}

      {/* WL-K15 — StB-only Auto-Kontierung (Buchungsvorschlag), collapsible per row. */}
      <div className="mt-3 border-t border-hairline pt-3">
        <button
          type="button"
          onClick={() => setShowKontierung((prev) => !prev)}
          aria-expanded={showKontierung}
          className="text-xs font-medium text-accent-ink hover:text-indigo-200"
        >
          {showKontierung ? 'Buchungsvorschlag ausblenden' : 'Buchungsvorschlag anzeigen'}
        </button>
        {showKontierung && (
          <div className="mt-2">
            <KontierungVorschlagPanel mandantUid={mandantUid} docId={item.docId} />
          </div>
        )}
      </div>
    </li>
  );
};

/** Lifecycle of a running/finished batch (M-UX-7.4 §2.2/§2.3). */
type BatchState =
  | { kind: 'idle' }
  | { kind: 'running'; done: number; total: number }
  | { kind: 'partial'; failed: number; total: number };

/**
 * Floating bulk-action bar (M-UX-7.4 §2.2 / D3). Rendered as a `fixed` overlay
 * so it never displaces the worklist (no layout jump), enter-animates ≤150ms,
 * and respects `prefers-reduced-motion`. `aria-live` lives on the single status
 * span (count/progress) to avoid double announcements.
 */
const BatchActionBar: React.FC<{
  count: number;
  batch: BatchState;
  onResolve: () => void;
  onIgnore: () => void;
  onClear: () => void;
}> = ({ count, batch, onResolve, onIgnore, onClear }) => {
  const { t } = useTranslation('common');
  const [entered, setEntered] = useState(false);
  // Flip a class on the frame after mount so the CSS transition actually runs
  // (a freshly-mounted element has no "from" state to animate otherwise).
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const running = batch.kind === 'running';
  const status = running
    ? t('cockpit.batch.progress', { done: batch.done, total: batch.total })
    : t('cockpit.batch.selectedCount', { count });

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-center px-4">
      <div
        role="region"
        aria-label={t('cockpit.batch.regionLabel', 'Stapelaktionen')}
        className={`pointer-events-auto flex max-w-full items-center gap-2 rounded-full border border-white/10 bg-[rgb(var(--surface-raised-rgb)/0.95)] px-3 py-2 shadow-2xl ring-1 ring-black/30 backdrop-blur-md transition duration-150 ease-out motion-reduce:transition-none ${
          entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
        }`}
      >
        <span aria-live="polite" className="whitespace-nowrap text-sm font-semibold text-primary tabular-nums">
          {status}
        </span>
        <span aria-hidden="true" className="h-5 w-px bg-surface-hover" />
        <button
          type="button"
          onClick={onResolve}
          disabled={running}
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800 focus-visible:ring-emerald-300 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <IconSuccess size={14} />
          {t('cockpit.batch.markResolved', 'Als geklärt markieren')}
        </button>
        <button
          type="button"
          onClick={onIgnore}
          disabled={running}
          className="inline-flex items-center gap-1.5 rounded-full bg-surface-inset px-3 py-1.5 text-sm font-medium text-primary transition hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800 focus-visible:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <IconViewOff size={14} />
          {t('cockpit.batch.markIgnored', 'Ignorieren')}
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={running}
          className="rounded-full px-3 py-1.5 text-sm font-medium text-muted transition hover:text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800 focus-visible:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {t('cockpit.batch.clear', 'Auswahl aufheben')}
        </button>
      </div>
    </div>
  );
};

/**
 * The worklist panel for one mandant's flagged items — rendered inside an
 * open {@link MandantCockpitGroup} (one instance per expanded group, not a
 * single "currently selected" mandant; the selection-rail model this
 * comment used to describe was replaced by the always-visible group list).
 */
const MandantWorklist: React.FC<{ mandantUid: string }> = ({ mandantUid }) => {
  const { t } = useTranslation('common');
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; data: CockpitWorklistResult }
  >({ status: 'loading' });

  // M-UX-7.4 — PARENT-owned batch selection (Set of `docId::checkId` keys),
  // roving-tabindex pointer, and batch lifecycle. Selection deliberately lives
  // here (not in FindingResolveControls / WorklistRow) so the shared resolve
  // component stays byte-identical (E3).
  const [selectedFindings, setSelectedFindings] = useState<Set<string>>(new Set());
  const [batch, setBatch] = useState<BatchState>({ kind: 'idle' });
  const [activeIndex, setActiveIndex] = useState(0);
  const rowRefs = useRef<Array<HTMLLIElement | null>>([]);
  const activeIndexRef = useRef(0);
  // Set before a post-batch refetch so focus can return to a sensible row once
  // the fresh worklist has rendered (D2).
  const focusReturnRef = useRef(false);

  // WL-B: extracted so a resolve/ignore/reopen write can trigger the SAME
  // fresh load ("nach Resolve Worklist neu laden", modules-cockpit-
  // intelligence.md W1-C) — a resolved finding can change ranking/counts or
  // remove the item entirely, so a local patch would not suffice.
  const loadWorklist = useCallback(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    getAdvisorCockpitWorklist(mandantUid)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [mandantUid]);

  useEffect(() => loadWorklist(), [loadWorklist]);

  // Reset selection + keyboard pointer whenever the mandant changes (§2.1 —
  // a stale key set would be a mis-selection hazard across mandants).
  useEffect(() => {
    setSelectedFindings(new Set());
    setBatch({ kind: 'idle' });
    activeIndexRef.current = 0;
    setActiveIndex(0);
  }, [mandantUid]);

  // Focus-return after a batch refetch (D2): once the fresh worklist is ready,
  // move DOM focus to the row at (clamped) activeIndex so keyboard triage can
  // continue without landing on document.body.
  useEffect(() => {
    if (state.status !== 'ready' || !focusReturnRef.current) return;
    focusReturnRef.current = false;
    const count = state.data.items.length;
    if (count === 0) return;
    const idx = Math.min(activeIndexRef.current, count - 1);
    rowRefs.current[idx]?.focus();
  }, [state]);

  const setActive = useCallback((i: number) => {
    activeIndexRef.current = i;
    setActiveIndex(i);
  }, []);

  const registerRow = useCallback((i: number, el: HTMLLIElement | null) => {
    rowRefs.current[i] = el;
  }, []);

  const clearSelection = useCallback(() => setSelectedFindings(new Set()), []);

  const toggleFinding = useCallback((docId: string, checkId: string) => {
    const key = findingKey(docId, checkId);
    setSelectedFindings((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const items = state.status === 'ready' ? state.data.items : [];

  // Tri-state row toggle: all of the doc's active findings selected ⇒ deselect
  // all; otherwise select all (§2/E2).
  const toggleRow = (docId: string) => {
    const item = items.find((i) => i.docId === docId);
    if (!item || item.findings.length === 0) return;
    const keys = item.findings.map((f) => findingKey(docId, f.checkId));
    setSelectedFindings((prev) => {
      const next = new Set(prev);
      const allSel = keys.every((k) => next.has(k));
      keys.forEach((k) => (allSel ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const moveFocus = (delta: number) => {
    const count = items.length;
    if (count === 0) return;
    const from = Math.min(activeIndexRef.current, count - 1);
    const next = Math.max(0, Math.min(from + delta, count - 1));
    setActive(next);
    rowRefs.current[next]?.focus();
  };

  /**
   * Runs the batch STRICTLY SEQUENTIALLY over the selected findings, reusing the
   * existing single-write callable (append-only GoBD trail — no bulk backend, no
   * rollback). Failures are collected; exactly ONE refetch happens at the end
   * (§2.3/E4). Selection is cleared before the refetch (stale-key guard).
   */
  const runBatch = async (type: 'resolved' | 'ignored') => {
    const targets: Array<{ docId: string; checkId: string }> = [];
    selectedFindings.forEach((key) => {
      const sep = key.indexOf('::');
      if (sep <= 0) return;
      targets.push({ docId: key.slice(0, sep), checkId: key.slice(sep + 2) });
    });
    if (targets.length === 0) return;

    const total = targets.length;
    setBatch({ kind: 'running', done: 0, total });
    let failed = 0;
    for (let i = 0; i < targets.length; i += 1) {
      const { docId, checkId } = targets[i];
      try {
        // eslint-disable-next-line no-await-in-loop -- append-only GoBD trail: strictly sequential, never Promise.all.
        await resolveCheckFinding({ mandantUid, docId, checkId, resolutionType: type, note: '' });
      } catch {
        failed += 1;
      }
      setBatch({ kind: 'running', done: i + 1, total });
    }
    setSelectedFindings(new Set());
    focusReturnRef.current = true;
    loadWorklist();
    setBatch(failed > 0 ? { kind: 'partial', failed, total } : { kind: 'idle' });
  };

  if (state.status === 'loading') {
    return <p className="px-1 py-6 text-sm text-muted">{t('cockpit.loading')}</p>;
  }
  if (state.status === 'error') {
    return <p className="px-1 py-6 text-sm text-[var(--tone-danger-fg)]">{t('cockpit.loadError')}</p>;
  }

  const { totalChecked, flaggedCount } = state.data;
  const selectedCount = selectedFindings.size;
  const effectiveActive = Math.min(activeIndex, Math.max(0, items.length - 1));

  const partialNotice =
    batch.kind === 'partial' ? (
      <div
        role="status"
        className="mb-4 flex items-center gap-2 rounded-md border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] px-3 py-2 text-sm text-[var(--tone-warning-fg)]"
      >
        <IconWarning size={16} className="shrink-0" />
        <span>{t('cockpit.batch.partialError', { failed: batch.failed, total: batch.total })}</span>
        <button
          type="button"
          onClick={() => setBatch({ kind: 'idle' })}
          className="ml-auto shrink-0 text-xs font-medium text-[var(--tone-warning-fg)] hover:text-amber-100"
        >
          {t('cockpit.batch.dismiss', 'Schließen')}
        </button>
      </div>
    ) : null;

  // „0 Findings" ⇒ Erfolgszustand „alles grün" (kein Fehler).
  if (flaggedCount === 0) {
    return (
      <div>
        {partialNotice}
        <div className="flex flex-col items-center justify-center rounded-lg bg-surface-raised px-6 py-12 text-center">
          <IconSuccess size={40} className="mb-3 text-[var(--tone-success-fg)]" />
          <h3 className="text-lg font-semibold text-primary">{t('cockpit.allGreenTitle')}</h3>
          <p className="mt-1 max-w-sm text-sm text-muted">
            {t('cockpit.allGreenBody', { count: totalChecked })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {partialNotice}
      <div className="mb-4 flex items-center gap-2 text-sm text-secondary">
        <IconWarning size={16} className="text-[var(--tone-warning-fg)]" />
        <span className="font-medium">
          {t('cockpit.summaryLine', { flagged: flaggedCount, total: totalChecked })}
        </span>
      </div>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">
        {t('cockpit.flaggedHeading')}
      </h2>
      <ul className="space-y-2">
        {items.map((item, idx) => (
          <WorklistRow
            key={item.docId}
            item={item}
            mandantUid={mandantUid}
            index={idx}
            isActive={idx === effectiveActive}
            selectedKeys={selectedFindings}
            onWorklistChanged={loadWorklist}
            onToggleFinding={toggleFinding}
            onToggleRow={toggleRow}
            onFocusRow={setActive}
            onMoveFocus={moveFocus}
            onClearSelection={clearSelection}
            registerRow={registerRow}
          />
        ))}
      </ul>

      {selectedCount > 0 && (
        <BatchActionBar
          count={selectedCount}
          batch={batch}
          onResolve={() => runBatch('resolved')}
          onIgnore={() => runBatch('ignored')}
          onClear={clearSelection}
        />
      )}
    </div>
  );
};

/**
 * One mandant's full-width group card (M-UX advisor-cockpit-fullwidth —
 * replaces the old mandant-selection rail so every mandant is visible at
 * once, not just the currently-selected one).
 *
 * The header (name + Ampel counters) is always rendered; the body — DATEV-
 * Stammdaten + the worklist — mounts LAZILY on first expand, so N mandanten
 * never fan out N simultaneous {@link getAdvisorCockpitWorklist} calls on
 * page load (no N+1 storm). Mandanten WITH findings start expanded (and are
 * therefore already loaded); "alles grün" mandanten start collapsed and only
 * load their (empty) worklist once the advisor opens them.
 */
const MandantCockpitGroup: React.FC<{
  summary: CockpitMandantSummary;
  /** Whether the group starts expanded — true for mandanten with findings. */
  defaultOpen: boolean;
}> = ({ summary, defaultOpen }) => {
  const { t } = useTranslation('common');
  const { currentUser, sellerData } = useAuth();
  // 🔴 M-GA-4 PFLICHTPUNKT 2: the DATEV-Stammdaten panel is a Cluster-F (DATEV)
  // surface embedded inside the Cluster-C (Cockpit) page. The page-level gate is
  // `canUseKanzleiCockpit`, but a Cockpit-GA flip must NOT leak the DATEV panel
  // while DATEV is still Beta — so the panel is ADDITIONALLY gated on
  // `canUseAdvisorDatev` (additive to the existing `everOpened` lazy-mount guard).
  const canDatev = canUseAdvisorDatev(currentUser?.uid, sellerData);
  const [open, setOpen] = useState(defaultOpen);
  // Sticky "has this group ever been opened" flag — flips to true on first
  // expand and never resets, so re-collapsing does NOT unmount (and thus
  // re-fetch) the worklist. Content stays mounted once loaded (mirrors the
  // `Collapsible` "kein Re-Mount-State-Verlust" pattern).
  const [everOpened, setEverOpened] = useState(defaultOpen);
  const panelId = useId();
  const name = summary.displayName || summary.mandantUid;

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) setEverOpened(true);
      return next;
    });
  };

  return (
    <Card as="section" padded={false} className="overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-surface-inset focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset sm:px-6"
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <IconChevronRight
            size={14}
            className={`shrink-0 text-muted motion-safe:transition-transform motion-safe:duration-150 ${
              open ? 'rotate-90' : ''
            }`}
          />
          <span className="truncate text-sm font-semibold text-primary sm:text-base">{name}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {summary.redCount > 0 && (
            <span className="inline-flex min-w-[1.25rem] justify-center rounded-full bg-[var(--tone-danger-bg)] px-1.5 text-xs font-bold text-[var(--tone-danger-fg)]">
              {summary.redCount}
            </span>
          )}
          {summary.yellowCount > 0 && (
            <span className="inline-flex min-w-[1.25rem] justify-center rounded-full bg-[var(--tone-warning-bg)] px-1.5 text-xs font-bold text-[var(--tone-warning-fg)]">
              {summary.yellowCount}
            </span>
          )}
          {summary.flaggedCount === 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--tone-success-fg)]">
              <IconSuccess size={14} />
              {t('cockpit.allGreenTitle')}
            </span>
          )}
        </span>
      </button>
      <div id={panelId} hidden={!open} className="border-t border-hairline px-4 py-4 sm:px-6">
        {/* Lazy-Load (§3): both the DATEV panel AND the worklist only mount once
            this group has been opened (`everOpened`, sticky). `Collapsible`
            keeps ITS OWN children mounted regardless of its `defaultOpen` prop
            (§5.8 "kein Re-Mount-State-Verlust") — so without this outer guard,
            `ClientDatevConfigPanel` (and its `getClientDatevConfig` call) would
            mount unconditionally for EVERY mandant as soon as the page renders,
            `hidden` only toggles CSS visibility, not React mounting. Gating the
            whole block on `everOpened` is what actually prevents the N+1 storm
            for collapsed "alles grün" mandanten. */}
        {everOpened && canDatev && (
          <div className="mb-4">
            <Collapsible title={t('cockpit.datevHeading')} defaultOpen={false}>
              <ClientDatevConfigPanel mandantUid={summary.mandantUid} />
            </Collapsible>
          </div>
        )}
        {everOpened && <MandantWorklist mandantUid={summary.mandantUid} />}
      </div>
    </Card>
  );
};

/** The data-fetching cockpit (mounted only for beta users). */
const AdvisorCockpitInner: React.FC = () => {
  const { t } = useTranslation('common');
  const [mandantenState, setMandantenState] = useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; mandanten: CockpitMandantSummary[] }
  >({ status: 'loading' });

  const loadMandanten = useCallback(() => {
    let cancelled = false;
    setMandantenState({ status: 'loading' });
    getAdvisorCockpitMandanten()
      .then((res) => {
        if (cancelled) return;
        setMandantenState({ status: 'ready', mandanten: res.mandanten });
      })
      .catch(() => {
        if (!cancelled) setMandantenState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadMandanten(), [loadMandanten]);

  const mandanten =
    mandantenState.status === 'ready' ? mandantenState.mandanten : [];

  // Sort so the advisor's attention lands on the worst client first without
  // a manual selection step (replaces the old rail): red-flagged mandanten
  // first, then yellow-flagged, "alles grün" mandanten last.
  const sortedMandanten = useMemo(() => {
    const rank = (m: CockpitMandantSummary) => (m.redCount > 0 ? 0 : m.yellowCount > 0 ? 1 : 2);
    return [...mandanten].sort((a, b) => {
      const rankDelta = rank(a) - rank(b);
      if (rankDelta !== 0) return rankDelta;
      if (b.flaggedCount !== a.flaggedCount) return b.flaggedCount - a.flaggedCount;
      return (a.displayName || a.mandantUid).localeCompare(b.displayName || b.mandantUid);
    });
  }, [mandanten]);

  return (
    <div className="min-h-screen bg-surface p-4 md:p-6">
      <div className="mx-auto max-w-screen-2xl">
        <header className="mb-6">
          <h1 className="flex items-center gap-3 text-2xl font-bold text-primary">
            <IconAdvisorCockpit size={20} className="text-accent-ink" />
            {t('cockpit.title')}
          </h1>
          <p className="mt-1 text-sm text-muted">{t('cockpit.subtitle')}</p>
        </header>

        {mandantenState.status === 'loading' && (
          <p className="text-sm text-muted">{t('cockpit.loading')}</p>
        )}
        {mandantenState.status === 'error' && (
          <p className="text-sm text-[var(--tone-danger-fg)]">{t('cockpit.loadError')}</p>
        )}

        {mandantenState.status === 'ready' && mandanten.length === 0 && (
          <div className="rounded-lg bg-surface-raised px-6 py-10 text-center text-sm text-muted">
            {t('cockpit.noMandanten')}
          </div>
        )}

        {mandantenState.status === 'ready' && mandanten.length > 0 && (
          <>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">
              {t('cockpit.mandantHeading')}
            </h2>
            <div className="space-y-4">
              {sortedMandanten.map((m) => (
                <MandantCockpitGroup key={m.mandantUid} summary={m} defaultOpen={m.flaggedCount > 0} />
              ))}
            </div>
          </>
        )}

        <p className="mt-8 max-w-3xl text-xs leading-relaxed text-faint">
          {t('cockpit.hint')}
        </p>
      </div>
    </div>
  );
};

/**
 * Outer visibility gate. Renders the data-fetching cockpit ONLY for Loop-B beta
 * users; everyone else gets a neutral „not available" placeholder (the server
 * enforces the real access — this is cosmetic only).
 */
const AdvisorCockpitPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser, sellerData } = useAuth();

  if (!canUseKanzleiCockpit(currentUser?.uid, sellerData)) {
    return (
      <div className="min-h-screen bg-surface p-6">
        <p className="text-sm text-muted">{t('cockpit.loadError')}</p>
      </div>
    );
  }
  return <AdvisorCockpitInner />;
};

export default AdvisorCockpitPage;
